import React, { useEffect, useState } from 'react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { Lock } from 'lucide-react';
import styles from './TeamCalendarGrid.module.css';

interface TeamCalendarGridProps {
  equipo_uuid: string;
  miembros: any[];
  currentUserId: string;
}

export function TeamCalendarGrid({ equipo_uuid, miembros, currentUserId }: Readonly<TeamCalendarGridProps>) {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Generate hours from 8 AM to 6 PM
  const hours = Array.from({ length: 11 }, (_, i) => i + 8);

  useEffect(() => {
    loadAppointments();
  }, [equipo_uuid]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      // Fetch appointments
      // Para simular UI sin datos reales, dejaremos un array vacío o mock si falla
      
      const { data, error } = await supabase
        .from('agendamiento_equipo')
        .select(`
          cita_equipo_id,
          fecha_hora_inicio,
          fecha_hora_fin,
          estado,
          paciente_uuid,
          cita_equipo_asistencias ( miembro_uuid )
        `)
        .eq('equipo_uuid', equipo_uuid)
        .gte('fecha_hora_inicio', new Date(new Date().setHours(0,0,0,0)).toISOString())
        .lte('fecha_hora_inicio', new Date(new Date().setHours(23,59,59,999)).toISOString());

      if (error) {
        console.warn('Could not fetch agendamiento_equipo, table might be empty or missing mock data:', error);
      } else {
        setAppointments(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isUserInAppointment = (asistencias: any[]) => {
    return asistencias.some((a: any) => a.miembro_uuid === currentUserId);
  };

  // Helper to calculate top and height based on 60px per hour
  const getPositionStyles = (inicio: string, fin: string) => {
    const startDate = new Date(inicio);
    const endDate = new Date(fin);
    
    const startHour = startDate.getHours() + startDate.getMinutes() / 60;
    const endHour = endDate.getHours() + endDate.getMinutes() / 60;
    
    // Grid starts at 8 AM
    const top = (startHour - 8) * 60;
    const height = (endHour - startHour) * 60;
    
    return { top: `${top}px`, height: `${height}px` };
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Cargando grilla multi-doctor...</div>;
  }

  // Filtrar solo los médicos (excluir secretarias de las columnas)
  const medicos = miembros.filter(m => m.rol_equipo.includes('clinico'));

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.calendarHeader}>
        <div className={styles.timeColumnHeader}>GMT-4</div>
        <div className={styles.doctorColumnsHeader}>
          {medicos.map(medico => (
            <div key={medico.miembro_uuid} className={styles.doctorHeaderCell}>
              <div className={styles.doctorAvatar}>
                {medico.perfiles_usuario?.nombres_completos?.[0] || 'M'}
              </div>
              <div className={styles.doctorName}>
                Dr. {medico.perfiles_usuario?.nombres_completos?.split(' ')[0]}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.calendarBody}>
        <div className={styles.timeColumn}>
          {hours.map(hour => (
            <div key={hour} className={styles.timeSlot}>
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        <div className={styles.gridContent}>
          {medicos.map(medico => {
            // Find appointments for this specific doctor
            const docAppointments = appointments.filter(apt => 
              apt.cita_equipo_asistencias?.some((a: any) => a.miembro_uuid === medico.miembro_uuid)
            );

            return (
              <div key={medico.miembro_uuid} className={styles.doctorColumn}>
                {docAppointments.map(apt => {
                  const stylesPos = getPositionStyles(apt.fecha_hora_inicio, apt.fecha_hora_fin);
                  const hasAccess = isUserInAppointment(apt.cita_equipo_asistencias);
                  
                  return (
                    <div 
                      key={apt.cita_equipo_id}
                      className={`${styles.appointment} ${hasAccess ? styles.aptMyPatient : styles.aptLocked}`}
                      style={stylesPos}
                    >
                      {hasAccess ? (
                        <>
                          <p className={styles.aptTitle}>Paciente Asignado</p>
                          <span className={styles.aptTime}>
                            {new Date(apt.fecha_hora_inicio).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        </>
                      ) : (
                        <Lock size={16} color="#94A3B8" />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
